import React, { useState } from 'react';

const Button = ({ 
  type = 'primary', 
  onClick, 
  children, 
  disabled = false,
  className = '' 
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (e) => {
    if (isLoading) return;
    
    setIsLoading(true);
    
    try {
      // Wait for the action to complete
      await onClick(e);
    } catch (error) {
      console.error('Button action failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStyles = () => {
    const baseStyles = {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0.75rem 1.5rem',
      borderRadius: '2rem',
      fontSize: '0.875rem',
      fontWeight: '600',
      border: 'none',
      outline: 'none',
      cursor: isLoading || disabled ? 'not-allowed' : 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
      overflow: 'hidden',
      minWidth: '120px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      opacity: isLoading || disabled ? 0.7 : 1,
    };

    const typeStyles = {
      primary: {
        backgroundColor: '#1a2a6c',
        color: 'white',
        border: 'none',
        ':hover': {
          backgroundColor: '#111d4a',
        }
      },
      secondary: {
        backgroundColor: 'white',
        color: '#1a2a6c',
        border: '1px solid #ddd',
        ':hover': {
          backgroundColor: '#f0f0f0',
        }
      },
      login: {
        backgroundColor: '#e67e22',
        color: 'white',
        border: 'none',
        ':hover': {
          backgroundColor: '#f39c12',
        }
      }
    };

    return { ...baseStyles, ...typeStyles[type] };
  };

  const styles = getStyles();

  return (
    <button
      className={`button ${type}-button ${className}`}
      onClick={handleClick}
      disabled={isLoading || disabled}
      style={{
        ...styles,
        transform: isLoading ? 'none' : undefined,
      }}
      onMouseEnter={(e) => {
        if (!isLoading && !disabled) {
          e.currentTarget.style.transform = 'translateY(-2px)';
          
          if (type === 'primary') {
            e.currentTarget.style.backgroundColor = '#111d4a';
          } else if (type === 'secondary') {
            e.currentTarget.style.backgroundColor = '#f0f0f0';
          } else if (type === 'login') {
            e.currentTarget.style.backgroundColor = '#f39c12';
          }
        }
      }}
      onMouseLeave={(e) => {
        if (!isLoading && !disabled) {
          e.currentTarget.style.transform = 'none';
          
          if (type === 'primary') {
            e.currentTarget.style.backgroundColor = '#1a2a6c';
          } else if (type === 'secondary') {
            e.currentTarget.style.backgroundColor = 'white';
          } else if (type === 'login') {
            e.currentTarget.style.backgroundColor = '#e67e22';
          }
        }
      }}
    >
      {isLoading ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div
            style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: `2px solid ${type === 'secondary' ? '#1a2a6c' : '#fff'}`,
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '8px',
            }}
          />
          Loading...
        </div>
      ) : (
        children
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

// Button Group component to organize multiple buttons
const ButtonGroup = ({ children, className = '' }) => {
  return (
    <div
      className={`button-group ${className}`}
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '1rem',
        margin: '1rem 0',
      }}
    >
      {children}
    </div>
  );
};

// Example usage
const ButtonExample = () => {
  // Simulate async actions
  const handlePrimaryClick = async () => {
    // Simulate API call
    return new Promise(resolve => setTimeout(resolve, 2000));
  };

  const handleSecondaryClick = async () => {
    // Simulate API call
    return new Promise(resolve => setTimeout(resolve, 1000));
  };

  const handleLoginClick = async () => {
    // Simulate login
    return new Promise(resolve => setTimeout(resolve, 1500));
  };

  return (
    <div>
      <h2>Button Examples</h2>
      <ButtonGroup>
        <Button type="primary" onClick={handlePrimaryClick}>
          Primary Button
        </Button>
        
        <Button type="secondary" onClick={handleSecondaryClick}>
          Secondary Button
        </Button>
        
        <Button type="login" onClick={handleLoginClick}>
          Login
        </Button>
      </ButtonGroup>
    </div>
  );
};

export { Button, ButtonGroup, ButtonExample };
export default Button;